import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';

import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';

import { AclAccess } from '@firestitch/acl';
import { BannerComponent } from '@firestitch/banner';
import { FsNavModule } from '@firestitch/nav';
import { FsStore } from '@firestitch/store';

import { filter, skip, Subject, switchMap, takeUntil, tap } from 'rxjs';

import { AclService, AppCallOutService, AppService, ExperienceService, SignoutService } from '@app/core';
import { SYSTEM_MODE_DEBUG } from '@common/consts';
import { Experience, Setting } from '@common/enums';
import { Account } from '@common/interfaces';

import { MatTooltipModule } from '@angular/material/tooltip';
import { BannerCollapseService, SidenavService } from '../../services';
import { AccountMenuSheetComponent } from '../account-menu-sheet/account-menu-sheet.component';
import { AppCallOutDialogComponent } from '../app-call-out-dialog';
import { HelpDialogComponent } from '../help-dialog';


@Component({
  selector: 'app-top-bar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    BannerComponent,
    RouterLink,
    MatMenuModule,
    MatDividerModule,
    FsNavModule,
    MatTooltipModule,
  ],
  templateUrl: './top-bar.component.html',
  styleUrl: './top-bar.component.scss',
})
export class TopBarComponent implements OnInit, OnChanges, OnDestroy {
  private _cdRef = inject(ChangeDetectorRef);
  private _experienceService = inject(ExperienceService);
  private _signoutService = inject(SignoutService);
  private _aclService = inject(AclService);
  private _store = inject(FsStore);
  private _sidenavService = inject(SidenavService);
  private _dialog = inject(MatDialog);
  private _appService = inject(AppService);
  private _bottomSheet = inject(MatBottomSheet);
  private _breakpointObserver = inject(BreakpointObserver);
  private _appCallOutService = inject(AppCallOutService);


  public experience: Experience = null;
  public isMobile = false;
  public isSmallView = false;
  public bannerCollapsed = signal<boolean>(false);
  public bannerActive = signal<boolean>(false);

  @Input() public account: Account = null;

  // Owned by the side nav since TU-T9400 — here only for the hamburger badge,
  // which is the sole unread indicator below the sidenav breakpoint.
  public readonly unreadCount = input<number>(0);

  @ViewChild(MatMenuTrigger) public menuTrigger!: MatMenuTrigger;

  public adminMenuItems = [];
  public systemMenuItems = [];

  public appCallOutEnabled = null;
  public appCallOutTitle = null;
  public appCallOutContent = null;
  public appCallOutColor = null;
  public Setting = Setting;

  private _bannerCollapseService = inject(BannerCollapseService);
  private _destroyRef = inject(DestroyRef);
  private _destroy$ = new Subject();

  constructor() {
    this._breakpointObserver.observe([
      Breakpoints.Handset, 
      Breakpoints.Small,
    ])
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        this.isMobile = result.matches;
      });

    this._breakpointObserver.observe([
      '(max-width: 1023px)'
    ])
      .pipe(takeUntil(this._destroy$))
      .subscribe((result) => {
        this.isSmallView = result.matches;
      });
  }

  public ngOnInit(): void {
    this._experienceService.experience$
      .pipe(takeUntil(this._destroy$))
      .subscribe((experience: Experience) => {
        this.experience = experience;
        this._initBannerState();
        this._setMenuItems();
      });

    this._store.observe('permissions')
      .pipe(
        skip(1),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this._setMenuItems();
      });

    this._store.observe('environment')
      .pipe(
        skip(1),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this._setMenuItems();
      });

    this._loadAppCallOutSettings();
    this._setMenuItems();

    // Reload the cached app config so freshly saved call-out settings
    // (color, title, content, visibility) are reflected without a full reload.
    this._appCallOutService.update$
      .pipe(
        switchMap(() => this._appService.load()),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        this._loadAppCallOutSettings();
        this._cdRef.markForCheck();
      });

    this._bannerCollapseService.changes$
      .pipe(
        filter((type) => type === this.bannerType),
        tap(() => this._initBannerState()),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  public get isParentExperience(): boolean {
    return this.experience === Experience.Parent;
  }

  public get bannerType(): string {
    return this.isParentExperience ? 'parentBanner' : 'organizationBanner';
  }

  public get accountPath(): string {
    return this.isParentExperience ? '/parent/account' : '/account';
  }

  public get bannerToggleLabel(): string {
    return this.bannerCollapsed() ? 'View announcement' : 'Hide announcement';
  }

  public get showAppCallOut() {
    return this.appCallOutEnabled && !this.isParentExperience;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.account) {
      this._setMenuItems();
    }
  }

  public sideNavClick(): void {
    this._sidenavService.toggle();
  }

  public toggleBanner(): void {
    this._bannerCollapseService.toggle(this.bannerType);
  }

  public openMenu() {
    if (this.isMobile) {
      this._bottomSheet.open(AccountMenuSheetComponent, {
        data: {
          isParentExperience: this.isParentExperience,
          adminMenuItems: this.adminMenuItems,
          systemMenuItems: this.systemMenuItems,
          accountPath: this.accountPath,
          appCallOutEnabled: this.appCallOutEnabled,
          appCallOutTitle: this.appCallOutTitle,
          support: () => this.support(),
          signOut: () => this.signOut(),
          openAppCallOut: () => this.openAppCallOut(),
        },
      });
    } else {
      this.menuTrigger.openMenu();
    }
  }

  public openAppCallOut() {
    this._dialog.open(AppCallOutDialogComponent, {
      data: {
        title: this.appCallOutTitle,
        content: this.appCallOutContent,
      },
    });
  }

  public signOut() {
    this._signoutService.signout();
  }

  public support() {
    if (this._aclService.hasAnyOrganizationLevelPermissions()) {
      return this._dialog.open(HelpDialogComponent);
    }

    const parentSupportUrl = this._appService.settings.helpParentUrl;
    window.open(parentSupportUrl, '_blank');
  }

  public ngOnDestroy(): void {
    this._destroy$.next(null);
    this._destroy$.complete();
  }

  private _initBannerState(): void {
    this.bannerCollapsed.set(this._bannerCollapseService.isCollapsed(this.bannerType));
    this.bannerActive.set(this._bannerCollapseService.isBannerActive(this.bannerType));
  }

  private _setMenuItems() {
    if (!this.isParentExperience) {
      if (this._aclService.hasPermissionAdmin() || this._aclService.hasPermissionSystem()) {
        this.adminMenuItems = [
          {
            label: 'Organizations',
            path: '/admin/organizations',
          },
          {
            label: 'Accounts',
            path: '/admin/accounts',
          },
          {
            label: 'Settings',
            path: '/admin/settings',
          },
          {
            label: 'Messages',
            path: '/admin/message',
          },
        ];
      }
      
      if (this._aclService.hasPermissionSystem(AclAccess.Full)) {
        this.systemMenuItems = [
          {
            label: 'Settings',
            path: '/system/settings',
          },
          {
            label: 'Crons',
            path: '/system/crons',
          },
          {
            label: 'File Manager',
            path: '/system/files',
          },
          {
            label: 'Processes',
            path: '/system/processes',
          },
          {
            label: 'Metrics',
            path: '/system/metrics',
          },
          {
            label: 'Logs',
            path: '/system/logs',
          },
          {
            label: 'Info',
            path: '/system/info',
          },
          {
            label: 'Build',
            path: '/system',
          },
        ];

        // Dev-only seeder page — hidden in production where settings.systemMode
        // is not debug (the route guard also redirects a direct hit there).
        if (this._appService.settings.systemMode === SYSTEM_MODE_DEBUG) {
          this.systemMenuItems.push({
            label: 'Testing',
            path: '/system/testing',
          });
        }
      }
    }
    
  }

  private _loadAppCallOutSettings() {
    this.appCallOutEnabled = this._appService.settings.appCallOutEnabled;
    this.appCallOutTitle = this._appService.settings.appCallOutTitle;
    this.appCallOutContent = this._appService.settings.appCallOutContent;
    this.appCallOutColor = this._appService.settings.appCallOutColor;
  }

}
